import { Component, ErrorInfo, ReactNode } from 'react';
import { Button, Result, Tooltip } from 'antd';
import { LogoutOutlined, MessageOutlined, RedoOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLogout } from '@/hooks/useLogout';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// 错误内容组件
const ErrorContent = ({ error }: { error: Error | null }) => {
  const [handleLogout] = useLogout();
  const { t } = useTranslation();

  /** 刷新当前页面 */
  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="flex items-center justify-center h-full">
      <Result
        status="error"
        title={t('public.pageErrorTitle')}
        subTitle={
          <div className="flex">
            <Tooltip title={String(error)} placement="top">
              <MessageOutlined className="mr-5px rotate-y-180" />
            </Tooltip>
            {t('public.pagepageErrorSubTitle')}
          </div>
        }
        extra={[
          <Button key="refresh" type="primary" icon={<RedoOutlined />} onClick={handleRefresh}>
            {t('public.refreshPage')}
          </Button>,
          <Button key="hard-refresh" icon={<LogoutOutlined />} onClick={handleLogout}>
            {t('public.signOut')}
          </Button>,
        ]}
      />
    </div>
  );
};

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(): State {
    return { hasError: true, error: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // 错误日志打印
    console.error('错误信息:', error, errorInfo);
    this.setState({ error });

    // 将错误信息上传至服务器
    this.sendErrorLog(error, errorInfo);
  }

  private async sendErrorLog(error: Error, errorInfo: ErrorInfo) {
    // 错误日志已在控制台输出，不需要发送到服务器
    // 如果将来需要错误日志收集功能，可以在这里添加
    // 目前后端没有 /log/create 接口，所以暂时移除
  }

  public render() {
    if (this.state.hasError) {
      return <ErrorContent error={this.state.error} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
